import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, FileText, RefreshCw, Truck } from 'lucide-react';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const PERIOD_OPTIONS = [
  { value: '1m', label: '1 mois' },
  { value: '3m', label: '3 mois' },
  { value: '1y', label: '1 an' },
  { value: 'all', label: 'Toutes périodes' }
];

const CourierStatistics = () => {
  const [period, setPeriod] = useState('1m');
  const [statistics, setStatistics] = useState([]);
  const [selectedCourierId, setSelectedCourierId] = useState('all');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/couriers/statistics`, {
        params: { period }
      });
      const rows = response.data?.statistics || [];
      setStatistics(rows);
      setGeneratedAt(response.data?.generated_at || null);
      if (selectedCourierId !== 'all' && !rows.some((row) => row.courier_id === selectedCourierId)) {
        setSelectedCourierId('all');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement des statistiques livreurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, [period]);

  const filteredRows = useMemo(() => {
    if (selectedCourierId === 'all') {
      return statistics;
    }
    return statistics.filter((row) => row.courier_id === selectedCourierId);
  }, [selectedCourierId, statistics]);

  const totalDeliveries = useMemo(
    () => filteredRows.reduce((sum, row) => sum + (row.total_deliveries || 0), 0),
    [filteredRows]
  );

  const handleExport = async (format) => {
    const normalized = format === 'xlsx' ? 'xlsx' : 'pdf';
    setExporting(true);
    try {
      const endpoint = normalized === 'xlsx'
        ? `${API}/admin/couriers/statistics/export/xlsx`
        : `${API}/admin/couriers/statistics/export/pdf`;
      const params = { period };
      if (selectedCourierId !== 'all') {
        params.courier_id = selectedCourierId;
      }

      const response = await axios.get(endpoint, {
        params,
        responseType: 'blob'
      });

      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `courier_statistics_${selectedCourierId !== 'all' ? selectedCourierId : period}.${normalized}`;
      const filename = filenameMatch?.[1] || fallbackName;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Export ${normalized.toUpperCase()} généré`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erreur export ${normalized.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Truck className="w-6 h-6 text-red-600" />
              Statistiques livreurs
            </CardTitle>
            <CardDescription>
              Performance par livreur basée sur les livraisons (succès, échecs, express)
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={loadStatistics}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('pdf')}
              disabled={exporting || loading || filteredRows.length === 0}
            >
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('xlsx')}
              disabled={exporting || loading || filteredRows.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Export XLSX
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <p className="text-sm text-gray-600 mb-1">Période</p>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Livreur</p>
            <Select value={selectedCourierId} onValueChange={setSelectedCourierId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les livreurs</SelectItem>
                {statistics.map((row) => (
                  <SelectItem key={row.courier_id} value={row.courier_id}>
                    {row.courier_name || row.courier_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Badge className="bg-cyan-100 text-cyan-700">
              Total livraisons affichées: {totalDeliveries}
            </Badge>
          </div>
        </div>

        {generatedAt && (
          <p className="text-xs text-gray-500">
            Généré le: {new Date(generatedAt).toLocaleString()}
          </p>
        )}

        {loading ? (
          <div className="py-10 text-center text-gray-500">Chargement des statistiques...</div>
        ) : filteredRows.length === 0 ? (
          <div className="py-10 text-center text-gray-500">Aucune statistique disponible pour cette sélection.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Livreur</TableHead>
                <TableHead>Matricule</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Réussies</TableHead>
                <TableHead>Échouées</TableHead>
                <TableHead>Express</TableHead>
                <TableHead>Montant total</TableHead>
                <TableHead>Montant standard</TableHead>
                <TableHead>Montant express</TableHead>
                <TableHead>Montant assuré</TableHead>
                <TableHead>Taux succès</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.courier_id}>
                  <TableCell className="font-medium">{row.courier_name || row.courier_id}</TableCell>
                  <TableCell>{row.matricule || '-'}</TableCell>
                  <TableCell>{row.total_deliveries}</TableCell>
                  <TableCell>{row.successful_deliveries}</TableCell>
                  <TableCell>{row.failed_deliveries}</TableCell>
                  <TableCell>{row.express_deliveries}</TableCell>
                  <TableCell>{Number(row.total_delivery_amount || 0).toFixed(2)} {row.amount_currency || 'FCFA'}</TableCell>
                  <TableCell>{Number(row.total_standard_amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{Number(row.total_express_amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{Number(row.total_insured_amount || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className="bg-green-100 text-green-700">
                      {Number(row.success_rate || 0).toFixed(2)}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default CourierStatistics;
